import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Option } from '@weave-design/menu';
import { useGlobalization } from '@Translations';
import {
	StyledMenu,
	StyledDataSourceTypesFilter,
} from './DataSourceFilter.styles';
import Dropdown from '@weave-design/dropdown';
import { InputLabel } from '@mui/material';
import { useIsFeatureEnabled, Features } from '@innovyze/stylovyze';
import { FLAGS } from '@Types';
import {
	PhysicalDataSourceType,
	VirtualDataSourceType,
	DataSourceTypesLabels,
} from '@Utils/constants';

interface Props {
	onDataSourceTypeChange: (sensorTypes: string[]) => void;
}

export const DataSourceFilter = ({ onDataSourceTypeChange }: Props) => {
	const { t } = useGlobalization();
	const isVirtualSensorsEnabled = useIsFeatureEnabled(
		FLAGS.ENABLE_VIRTUAL_SENSORS_ON_SENSOR_CONFIGURATION as keyof Features,
	);

	const DATA_SOURCE_TYPE_LIST = useMemo(
		() => [
			...Object.values(PhysicalDataSourceType).map(val => ({
				key: val,
				label: val,
			})),
			...(isVirtualSensorsEnabled
				? Object.values(VirtualDataSourceType)
						.filter(val => val !== VirtualDataSourceType.EMAGIN)
						.map(val => ({
							key: val,
							// @ts-ignore
							label: DataSourceTypesLabels[val],
						}))
				: []),
		],
		[],
	);

	const dropdownRef = useRef<HTMLDivElement>(null);
	const [selectedDataSourceTypes, setDataSourceTypes] = useState<string[]>(
		[],
	);

	const [dropdownPlaceholder, setDropdownPlaceholder] = useState<string>(
		t('All'),
	);

	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const onDataSourceTypeSelect = (sensorType: string) => {
		const existsSensorType = selectedDataSourceTypes.includes(sensorType);
		let newSelectedTypes: string[] = [];
		if (existsSensorType) {
			newSelectedTypes = selectedDataSourceTypes.filter(
				sType => sType !== sensorType && sType !== 'All',
			);
		} else {
			newSelectedTypes = [...selectedDataSourceTypes, sensorType];
		}

		if (newSelectedTypes.length === DATA_SOURCE_TYPE_LIST.length) {
			newSelectedTypes.push('All');
		}
		setDataSourceTypes(newSelectedTypes);
		onDataSourceTypeChange(newSelectedTypes);
	};

	const selectAllSensorTypes = () => {
		if (selectedDataSourceTypes.includes('All')) {
			setDataSourceTypes([]);
			onDataSourceTypeChange([]);
			return;
		}
		const newSensorTypes = [
			...DATA_SOURCE_TYPE_LIST.map(type => type.key),
			'All',
		];
		setDataSourceTypes(newSensorTypes);
		onDataSourceTypeChange(newSensorTypes);
	};

	useEffect(() => {
		const numberOfSelectedTypes = selectedDataSourceTypes.length;
		const totalSensorTypes = DATA_SOURCE_TYPE_LIST.length;
		if (numberOfSelectedTypes === 0) {
			setDropdownPlaceholder(t('All types'));
		}
		if (numberOfSelectedTypes === 1) {
			setDropdownPlaceholder(
				// @ts-ignore
				DataSourceTypesLabels[selectedDataSourceTypes[0]] ??
					selectedDataSourceTypes[0],
			);
		}

		if (
			numberOfSelectedTypes > 1 &&
			numberOfSelectedTypes < totalSensorTypes
		) {
			setDropdownPlaceholder(t('Multiple'));
		}

		if (numberOfSelectedTypes >= totalSensorTypes) {
			setDropdownPlaceholder(t('All'));
		}
	}, [selectedDataSourceTypes]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsMenuOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<StyledDataSourceTypesFilter ref={dropdownRef}>
			<InputLabel>{t('Data Source Types')}</InputLabel>
			<Dropdown
				value={dropdownPlaceholder}
				onClick={() => setIsMenuOpen(!isMenuOpen)}
			/>
			{isMenuOpen ? (
				<StyledMenu
					multiple
					checkmark
					selected={selectedDataSourceTypes}>
					<Option id="All" onClick={selectAllSensorTypes}>
						{t('All')}
					</Option>
					{DATA_SOURCE_TYPE_LIST.map(sensorType => (
						<Option
							key={sensorType.key}
							id={sensorType.key}
							onClick={() =>
								onDataSourceTypeSelect(sensorType.key)
							}>
							{t(sensorType.label)}
						</Option>
					))}
				</StyledMenu>
			) : null}
		</StyledDataSourceTypesFilter>
	);
};
